import React, { useState, useEffect, useMemo } from 'react';
import { Download, Loader2, Filter, Building2, Package } from 'lucide-react';
import { salesReportsAPI, branchAPI } from '../../services/api';
import DateRangeFilter from './DateRangeFilter';
import { toast } from 'sonner';

const BranchProductSalesReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [branches, setBranches] = useState([]);
  const [showColumns, setShowColumns] = useState({
    sku: true,
    product_name: true,
    category: true,
    sub_category: false,
    hsn_code: false,
    unit: true,
  });

  useEffect(() => {
    branchAPI.getAll().then(setBranches).catch(() => {});
  }, []);

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedBranch, selectedCategory, selectedSubCategory]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.from) params.from_date = dateRange.from;
      if (dateRange.to) params.to_date = dateRange.to;
      if (selectedBranch) params.branch = selectedBranch;
      if (selectedCategory) params.category = selectedCategory;
      if (selectedSubCategory) params.sub_category = selectedSubCategory;
      
      const result = await salesReportsAPI.getBranchProductSales(params);
      setData(result);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = {
        columns: Object.entries(showColumns).filter(([, v]) => v).map(([k]) => k).join(','),
      };
      if (dateRange.from) params.from_date = dateRange.from;
      if (dateRange.to) params.to_date = dateRange.to;
      if (selectedBranch) params.branch = selectedBranch;
      if (selectedCategory) params.category = selectedCategory;
      
      const blob = await salesReportsAPI.exportBranchProductSales(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `branch_product_sales_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Report exported');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const displayBranches = useMemo(() => {
    if (!data?.branches) return [];
    if (selectedBranch) {
      return data.branches.filter(b => b.id === selectedBranch);
    }
    return data.branches;
  }, [data?.branches, selectedBranch]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#24549c' }} />
      </div>
    );
  }

  return (
    <div data-testid="branch-product-sales-report">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'DM Sans' }}>
            <Building2 className="w-5 h-5" style={{ color: '#24549c' }} />
            Branch-wise Product Sales
          </h2>
          <p className="text-xs text-gray-500">Product sales breakdown by ship-from branch</p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || !data?.items?.length}
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50"
          style={{ backgroundColor: '#24549c' }}
          data-testid="export-branch-sales-btn"
        >
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Export Excel
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 rounded-xl p-4 mb-5">
        <div className="flex flex-wrap items-center gap-4">
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
          
          <div>
            <label className="text-[10px] text-gray-500 font-semibold uppercase mb-1 block">Branch</label>
            <select
              value={selectedBranch}
              onChange={e => setSelectedBranch(e.target.value)}
              className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white min-w-[150px]"
              data-testid="branch-filter"
            >
              <option value="">All Branches</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-[10px] text-gray-500 font-semibold uppercase mb-1 block">Category</label>
            <select
              value={selectedCategory}
              onChange={e => { setSelectedCategory(e.target.value); setSelectedSubCategory(''); }}
              className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white min-w-[150px]"
              data-testid="category-filter"
            >
              <option value="">All Categories</option>
              {(data?.filters?.categories || []).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-[10px] text-gray-500 font-semibold uppercase mb-1 block">Sub-Category</label>
            <select
              value={selectedSubCategory}
              onChange={e => setSelectedSubCategory(e.target.value)}
              className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white min-w-[150px]"
              data-testid="subcategory-filter"
            >
              <option value="">All Sub-Categories</option>
              {(data?.filters?.sub_categories || []).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          
          {/* Column Selector */}
          <div className="relative group">
            <label className="text-[10px] text-gray-500 font-semibold uppercase mb-1 block">Columns</label>
            <button className="flex items-center gap-1.5 px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white hover:bg-gray-50">
              <Filter className="w-3.5 h-3.5" /> Select Columns
            </button>
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 hidden group-hover:block z-10 min-w-[180px]">
              {Object.entries({
                sku: 'SKU',
                product_name: 'Product Name',
                category: 'Category',
                sub_category: 'Sub-Category',
                hsn_code: 'HSN Code',
                unit: 'Unit',
              }).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 py-1 text-xs cursor-pointer hover:bg-gray-50 px-1 rounded">
                  <input
                    type="checkbox"
                    checked={showColumns[key]}
                    onChange={e => setShowColumns(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {[
          { label: 'Total Products', value: data?.summary?.total_products || 0 },
          { label: 'Total Qty Sold', value: (data?.summary?.grand_total_qty || 0).toLocaleString('en-IN') },
          { label: 'Total Value', value: `₹${(data?.summary?.grand_total_value || 0).toLocaleString('en-IN')}` },
          { label: 'Branches', value: displayBranches.length },
        ].map((kpi, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-[10px] text-gray-500 font-semibold uppercase">{kpi.label}</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase sticky left-0 bg-gray-50">#</th>
                {showColumns.sku && <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase">SKU</th>}
                {showColumns.product_name && <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase min-w-[200px]">Product</th>}
                {showColumns.category && <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase">Category</th>}
                {showColumns.sub_category && <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase">Sub-Cat</th>}
                {showColumns.hsn_code && <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase">HSN</th>}
                {showColumns.unit && <th className="px-3 py-3 text-center text-[10px] font-semibold text-gray-600 uppercase">Unit</th>}
                {displayBranches.map(b => (
                  <th key={b.id} className="px-3 py-3 text-center text-[10px] font-semibold text-gray-600 uppercase min-w-[120px]" colSpan={2}>
                    {b.name.split(' ')[0]}
                  </th>
                ))}
                <th className="px-3 py-3 text-center text-[10px] font-semibold text-gray-600 uppercase bg-blue-50" colSpan={2}>Total</th>
              </tr>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th></th>
                {showColumns.sku && <th></th>}
                {showColumns.product_name && <th></th>}
                {showColumns.category && <th></th>}
                {showColumns.sub_category && <th></th>}
                {showColumns.hsn_code && <th></th>}
                {showColumns.unit && <th></th>}
                {displayBranches.map(b => (
                  <React.Fragment key={b.id}>
                    <th className="px-2 py-1 text-[9px] text-gray-500">Qty</th>
                    <th className="px-2 py-1 text-[9px] text-gray-500">Value</th>
                  </React.Fragment>
                ))}
                <th className="px-2 py-1 text-[9px] text-gray-500 bg-blue-50">Qty</th>
                <th className="px-2 py-1 text-[9px] text-gray-500 bg-blue-50">Value</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((item, idx) => (
                <tr key={item.product_id || idx} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-400 sticky left-0 bg-white">{idx + 1}</td>
                  {showColumns.sku && <td className="px-3 py-2 font-mono text-gray-500">{item.sku}</td>}
                  {showColumns.product_name && <td className="px-3 py-2 font-medium">{item.product_name}</td>}
                  {showColumns.category && <td className="px-3 py-2 text-gray-500">{item.category}</td>}
                  {showColumns.sub_category && <td className="px-3 py-2 text-gray-500">{item.sub_category}</td>}
                  {showColumns.hsn_code && <td className="px-3 py-2 text-gray-500">{item.hsn_code}</td>}
                  {showColumns.unit && <td className="px-3 py-2 text-center text-gray-500">{item.unit}</td>}
                  {displayBranches.map(b => {
                    const bd = item.branches?.[b.id] || {};
                    return (
                      <React.Fragment key={b.id}>
                        <td className="px-2 py-2 text-right">{bd.qty || '-'}</td>
                        <td className="px-2 py-2 text-right text-gray-600">{bd.value ? `₹${bd.value.toLocaleString('en-IN')}` : '-'}</td>
                      </React.Fragment>
                    );
                  })}
                  <td className="px-2 py-2 text-right font-semibold bg-blue-50">{item.total_qty}</td>
                  <td className="px-2 py-2 text-right font-semibold bg-blue-50">₹{item.total_value?.toLocaleString('en-IN')}</td>
                </tr>
              ))}
              {(!data?.items || data.items.length === 0) && (
                <tr>
                  <td colSpan={20} className="px-3 py-10 text-center text-gray-400">
                    <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    No sales data found for the selected filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BranchProductSalesReport;
